import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import {
  ApiRequests,
  ApiRequestsTypes,
  DatabaseMastheadIcon,
  FeatureFlagsContext,
  getDatabaseIconTooltip,
  HeaderLabelChipStyle,
  PlaceHeaderMetadataItem,
  PlacePageActionsMenu,
  PlacePageHeader,
  PlacePageMastheadContainer,
  PlacePageMastheadRowContainer,
  PlacePageTitle,
  PlacePageTitleContainer,
  SiteUrlsContext,
  UIActionsContext,
  useTelemetrySender,
} from '../../../data-catalog-shared';
import { LabelWrapper } from '../Labels/LabelsHookHelper';
import { CreateContentItemFromTelemetryEvent } from '../TablePlacePage/CreateContentItemFromTelemetryEvent';
import {
  CreateDocumentFromResourceButton,
  RESOURCE_TYPE_DATABASE,
} from '../TablePlacePage/CreateWorkbookFromCatalogButtons';
import { DatabasesDisplayPlacePageData } from '../database/types/DatabasesDisplayPlacePageData';
import { PlaceHeaderMetadataProject } from '../placePage/PlaceHeaderMetadataProject';
import { PlaceHeaderMetadataTableauUser } from '../placePage/PlaceHeaderMetadataTableauUser';
import { PlacePageIconContainer } from '../placePage/PlacePageIconContainer';

export type DatabasePlacePageHeaderProps = {
  readonly apiRequests: ApiRequests;
  readonly database: DatabasesDisplayPlacePageData;
  readonly hideLaunchEditOnMobile?: boolean;
  readonly newFlowFromCatalog?: boolean;
  readonly projectIdForNewDocuments?: string;
};

export function DatabasePlacePageHeader(
  props: DatabasePlacePageHeaderProps
): JSX.Element {
  const uiActions = React.useContext(UIActionsContext);
  const siteUrls = React.useContext(SiteUrlsContext);
  const sendCreateEvent = useTelemetrySender<CreateContentItemFromTelemetryEvent>({ type: '', content_type: '', data_type: '', connection_type: '' });

  const editingIsEnabled: boolean = props.apiRequests.isSiteCreatorOrHigher() && !props.hideLaunchEditOnMobile;
  const canCreateFlowFromDatabase: boolean = editingIsEnabled && !!props.newFlowFromCatalog;

  return (
    <FeatureFlagsContext.Consumer>
      {
        featureFlags =>
          <PlacePageHeader>
            <PlacePageTitleContainer>
              <PlacePageIconContainer title={getDatabaseIconTooltip(props.database)}>
                <DatabaseMastheadIcon database={props.database} />
              </PlacePageIconContainer>
              <PlacePageMastheadContainer>
                <PlacePageMastheadRowContainer>
                  <PlacePageTitle title={props.database.name || undefined}>
                    {props.database.name}
                  </PlacePageTitle>
                  <PlacePageActionsMenu>
                    {uiActions.renderDatabasesActionMenu(props.database)}
                  </PlacePageActionsMenu>
                </PlacePageMastheadRowContainer>
                <PlacePageMastheadRowContainer>
                  <DatabaseContact database={props.database} />
                  {featureFlags.databasesInProjectsEnabled && <DatabaseProject database={props.database} apiRequests={props.apiRequests} siteUrls={siteUrls} />}
                  <LabelWrapper itemInfo={props.database} category={'all'} isEager={true} kind={'chip'} wrapper={(e) => <HeaderLabelChipStyle>{e}</HeaderLabelChipStyle>} />
                </PlacePageMastheadRowContainer>
              </PlacePageMastheadContainer>
            </PlacePageTitleContainer>
            <CreateDocumentFromResourceButton
              apiRequests={props.apiRequests}
              siteUrls={siteUrls}
              isEmbedded={!!props.database.isEmbedded}
              resourceId={props.database.id}
              resourceType={RESOURCE_TYPE_DATABASE}
              databaseClass={props.database.connectionType}
              testId={'CreateDocumentFromDatabaseButton'}
              title={Messages.NewWorkbookButton()}
              canCreateWorkbook={editingIsEnabled}
              canCreateFlow={canCreateFlowFromDatabase}
              projectIdForNewDocuments={props.projectIdForNewDocuments}
              sendCreateDocumentEvent={sendCreateEvent}
            />
          </PlacePageHeader>
      }
    </FeatureFlagsContext.Consumer>
  );
}

function DatabaseContact(props: {
  database: DatabasesDisplayPlacePageData;
}): JSX.Element {
  return (
    <PlaceHeaderMetadataItem>
      <PlaceHeaderMetadataTableauUser
        tableauUser={props.database.contact}
        labelText={Messages.GridHeaderContact()}
      />
    </PlaceHeaderMetadataItem>
  );
}

export function DatabaseProject(props: {
  database: DatabasesDisplayPlacePageData,
  apiRequests: ApiRequests,
  siteUrls: SiteUrls
}): JSX.Element | null {
  const [container, setContainer] = React.useState<ApiRequestsTypes.GetContainerForContentResult>();

  React.useEffect(
    () => {
      props.apiRequests
        .getContainerForContent({ contentType: 'database', contentId: props.database.vizportalId })
        .then(container => setContainer(container))
        .catch(() => setContainer(undefined));
    },
    [props.database, props.apiRequests]
  );

  return (container && container.type === 'PROJECT')
    ? (
      <PlaceHeaderMetadataItem>
        <PlaceHeaderMetadataProject projectId={container.id} projectName={container.name} siteUrls={props.siteUrls} />
      </PlaceHeaderMetadataItem>
    )
    : null;
}
